import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { MeetingsService, RequestService } from '@app/core/services';
import { ChangableComponent } from '@models/changable.component';
import { regex } from '@shared/utils';
import { Meeting } from 'lingo2-models';
import { Router as ContentRouter } from 'lingo2-models/dist/content/router';
import { OnUiButtonState } from 'onclass-ui';
import { Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-invite-footer',
  templateUrl: './invite-footer.component.html',
  styleUrls: ['./invite-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteFooterComponent extends ChangableComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private requestService = inject(RequestService);
  private meetingService = inject(MeetingsService);
  private router = inject(Router);
  private request = inject(RequestService);

  @Input() meeting: Meeting;

  public emailsControl: UntypedFormControl;
  public emailButtonState: OnUiButtonState;
  public emailSent = false;
  public copied = false;

  private _onEmailSent: Subscription;
  private _onCopied: Subscription;

  ngOnInit(): void {
    this.emailButtonState = 'default';
    this.emailsControl = this.fb.control(
      {
        value: '',
        disabled: true,
      },
      {
        updateOn: 'blur',
        validators: [this.emailsValidator.bind(this)],
      },
    );
  }

  public get meetingUrl(): string {
    return this.request.host + this.router.createUrlTree(ContentRouter.meetingJoinRoute(this.meeting)).toString();
  }

  public onCopied(event: any) {
    this.copied = true;
    this.markForCheck();

    this.clearTimeout(this._onCopied);
    this._onCopied = this.setTimeout(() => {
      this.copied = false;
      this.markForCheck();
    }, 2000);
  }

  protected onEmailSent() {
    this.emailSent = true;
    this.markForCheck();

    this.clearTimeout(this._onEmailSent);
    this._onEmailSent = this.setTimeout(() => {
      this.emailSent = false;
      this.markForCheck();
    }, 2000);
  }

  public inviteViaEmail(): void {
    if (this.emailsControl.invalid || this.emailsControl.disabled) {
      return;
    }

    this.emailButtonState = 'progress';
    const emails = this.parseEmails(this.emailsControl.value);

    this.meetingService
      .inviteUsers(this.meeting.id, [])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (item) => {
          this.emailButtonState = 'default';
          this.emailSent = true;
          this.markForCheck();
          this.onEmailSent();
        },
        error: (error: any) => {
          this.emailButtonState = 'fail';
        },
      });
  }

  private parseEmails(emails: string): string[] {
    return emails
      .split(',')
      .map((x) => x.trim())
      .filter((x) => !!x);
  }

  private emailsValidator(control: UntypedFormControl): ValidationErrors | null {
    const emails = this.parseEmails(control.value);
    const isOk = emails.every((email) => regex.email.test(email));
    return !isOk ? { emails: { value: true, text: 'meetings.invite.emails-error' } } : null;
  }
}
